import { useState, useEffect, useRef } from 'react';
import '../Styles/joinModel.css';

const INDUSTRY_OPTIONS = [
  'Information Technology (IT)',
  'IT Enabled Services (ITES/BPO/KPO)',
  'Recruitment & Staffing',
  'Human Resources Consulting',
  'Business Process Outsourcing (BPO)',
  'Manufacturing',
  'Automobile & Auto Components',
  'Electronics & Electrical',
  'Construction & Infrastructure',
  'Real Estate',
  'Banking, Financial Services & Insurance (BFSI)',
  'FinTech',
  'Healthcare & Hospitals',
  'Pharmaceuticals',
  'Biotechnology',
  'Education / EdTech',
  'E-commerce',
  'Retail',
  'Wholesale & Distribution',
  'Logistics & Supply Chain',
  'Transportation',
  'Travel & Tourism',
  'Hospitality & Hotels',
  'Food & Beverage',
  'Agriculture & AgriTech',
  'Textile & Apparel',
  'Media & Entertainment',
  'Advertising & Marketing',
  'Digital Marketing',
  'Telecommunications',
  'Energy & Utilities',
  'Renewable Energy',
  'Mining & Metals',
  'Chemicals',
  'Oil & Gas',
  'Legal Services',
  'Security Services',
  'Event Management',
  'NGO / Non-Profit',
  'Research & Development (R&D)',
  'Import & Export',
  'Others',
];

const STATE_OPTIONS = [
  'Andhra Pradesh',
  'Arunachal Pradesh',
  'Assam',
  'Bihar',
  'Chhattisgarh',
  'Goa',
  'Gujarat',
  'Haryana',
  'Himachal Pradesh',
  'Jharkhand',
  'Karnataka',
  'Kerala',
  'Madhya Pradesh',
  'Maharashtra',
  'Manipur',
  'Meghalaya',
  'Mizoram',
  'Nagaland',
  'Odisha',
  'Punjab',
  'Rajasthan',
  'Sikkim',
  'Tamil Nadu',
  'Telangana',
  'Tripura',
  'Uttar Pradesh',
  'Uttarakhand',
  'West Bengal',
  'Andaman and Nicobar Islands',
  'Chandigarh',
  'Dadra and Nagar Haveli and Daman and Diu',
  'Delhi',
  'Jammu and Kashmir',
  'Ladakh',
  'Lakshadweep',
  'Puducherry',
];

const initialFormData = {
  fullName: '',
  designation: '',
  company: '',
  contactNumber: '',
  personalEmail: '',
  officialEmail: '',
  industry: '',
  gender: '',
  linkedin: '',
  state: '',
  city: '',
  image: null,
};

/* Reusable type-to-filter dropdown, used for Nature of Industry and State.
   Typing narrows the list to options starting with what's typed
   (e.g. typing "t" surfaces "Tamil Nadu", "Telangana", ...). */
function SearchableDropdown({ id, options, value, onSelect, placeholder, hasError }) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState(value || '');
  const wrapperRef = useRef(null);

  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
        setQuery(value || '');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [value]);

  const filteredOptions = query
    ? options.filter((opt) => opt.toLowerCase().startsWith(query.toLowerCase()))
    : options;

  const handleInputChange = (e) => {
    setQuery(e.target.value);
    setIsOpen(true);
  };

  const handleSelect = (label) => {
    onSelect(label);
    setQuery(label);
    setIsOpen(false);
  };

  return (
    <div className="join-modal-custom-select" ref={wrapperRef}>
      <div className={`join-modal-custom-trigger ${hasError ? 'join-modal-input-error' : ''}`}>
        <input
          id={id}
          type="text"
          className="join-modal-custom-input"
          placeholder={placeholder}
          value={query}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          autoComplete="off"
        />
        <span
          className={`join-modal-custom-arrow ${isOpen ? 'open' : ''}`}
          onClick={() => setIsOpen((prev) => !prev)}
        />
      </div>

      {isOpen && (
        <ul className="join-modal-custom-list" role="listbox">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((label) => (
              <li
                key={label}
                role="option"
                aria-selected={value === label}
                className={`join-modal-custom-option ${value === label ? 'selected' : ''}`}
                onClick={() => handleSelect(label)}
              >
                {label}
              </li>
            ))
          ) : (
            <li className="join-modal-custom-option join-modal-custom-empty">
              No matches found
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

function JoinModal({ isOpen, onClose }) {
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0] || null;
    setFormData((prev) => ({ ...prev, image: file }));
    setErrors((prev) => ({ ...prev, image: '' }));
  };

  const setField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validate = (data) => {
    const err = {};

    if (!/^[A-Za-z' -]{2,100}$/.test(data.fullName.trim())) {
      err.fullName = 'Please enter your full name.';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(data.personalEmail) || data.personalEmail.length > 254) {
      err.personalEmail = 'Please enter a valid personal email address.';
    }

    if (
      !emailRegex.test(data.officialEmail) ||
      data.officialEmail.length > 254 ||
      (data.officialEmail &&
        data.personalEmail &&
        data.officialEmail.toLowerCase() === data.personalEmail.toLowerCase())
    ) {
      err.officialEmail = 'Please enter a valid official email address.';
    }

    if (!/^\+?[0-9]{10,15}$/.test(data.contactNumber.trim())) {
      err.contactNumber = 'Please enter a valid contact number (10–15 digits).';
    }

    if (!/^[A-Za-z0-9&/.\-() ]{2,100}$/.test(data.designation.trim())) {
      err.designation = 'Please enter your designation.';
    }

    if (!/^[A-Za-z0-9&.,'\- ]{2,150}$/.test(data.company.trim())) {
      err.company = 'Please enter your company name.';
    }

    if (!data.industry || !INDUSTRY_OPTIONS.includes(data.industry)) {
      err.industry = 'Please select an industry.';
    }

    if (!data.gender) {
      err.gender = 'Please select your gender.';
    }

    if (!/^https:\/\/(www\.)?linkedin\.com\//.test(data.linkedin.trim())) {
      err.linkedin = 'Please enter a valid LinkedIn profile URL.';
    }

    if (!data.state || !STATE_OPTIONS.includes(data.state)) {
      err.state = 'Please select your state.';
    }

    if (!/^[A-Za-z.\- ]{2,100}$/.test(data.city.trim())) {
      err.city = 'Please enter your city.';
    }

    if (!data.image) {
      err.image = 'Please upload a JPG, JPEG, PNG, or WebP image (maximum 5 MB).';
    } else {
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      const maxSizeBytes = 5 * 1024 * 1024;
      if (!allowedTypes.includes(data.image.type) || data.image.size > maxSizeBytes) {
        err.image = 'Please upload a JPG, JPEG, PNG, or WebP image (maximum 5 MB).';
      }
    }

    return err;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationErrors = validate(formData);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    console.log('Join form submitted:', formData);
    setFormData(initialFormData);
    setErrors({});
    onClose();
  };

  const handleClose = () => {
    setFormData(initialFormData);
    setErrors({});
    onClose();
  };

  return (
    <div className="join-modal-overlay" onClick={handleClose}>
      <div className="join-modal-card" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="join-modal-close"
          onClick={handleClose}
          aria-label="Close"
        >
          &times;
        </button>

        <h2 className="join-modal-title">
          Join Our Community 
          
        </h2>

        <form className="join-modal-form" onSubmit={handleSubmit} noValidate>
          <div className="join-modal-row">
            <div className="join-modal-field">
              <label htmlFor="fullName">
                Full Name<span>*</span>
              </label>
              <input
                id="fullName"
                type="text"
                name="fullName"
                placeholder="Full name"
                value={formData.fullName}
                onChange={handleChange}
                className={errors.fullName ? 'join-modal-input-error' : ''}
              />
              {errors.fullName && <p className="join-modal-error">{errors.fullName}</p>}
            </div>
            <div className="join-modal-field">
              <label htmlFor="designation">
                Designation<span>*</span>
              </label>
              <input
                id="designation"
                type="text"
                name="designation"
                placeholder="Designation"
                value={formData.designation}
                onChange={handleChange}
                className={errors.designation ? 'join-modal-input-error' : ''}
              />
              {errors.designation && (
                <p className="join-modal-error">{errors.designation}</p>
              )}
            </div>
          </div>

          <div className="join-modal-row">
            <div className="join-modal-field">
              <label htmlFor="company">
                Company<span>*</span>
              </label>
              <input
                id="company"
                type="text"
                name="company"
                placeholder="Company"
                value={formData.company}
                onChange={handleChange}
                className={errors.company ? 'join-modal-input-error' : ''}
              />
              {errors.company && <p className="join-modal-error">{errors.company}</p>}
            </div>
            <div className="join-modal-field">
              <label htmlFor="contactNumber">
                Contact Number<span>*</span>
              </label>
              <input
                id="contactNumber"
                type="tel"
                name="contactNumber"
                placeholder="Contact Number"
                value={formData.contactNumber}
                onChange={handleChange}
                className={errors.contactNumber ? 'join-modal-input-error' : ''}
              />
              {errors.contactNumber && (
                <p className="join-modal-error">{errors.contactNumber}</p>
              )}
            </div>
          </div>

          <div className="join-modal-row">
            <div className="join-modal-field">
              <label htmlFor="personalEmail">
                Personal Email<span>*</span>
              </label>
              <input
                id="personalEmail"
                type="email"
                name="personalEmail"
                placeholder="Personal Email"
                value={formData.personalEmail}
                onChange={handleChange}
                maxLength={254}
                className={errors.personalEmail ? 'join-modal-input-error' : ''}
              />
              {errors.personalEmail && (
                <p className="join-modal-error">{errors.personalEmail}</p>
              )}
            </div>
            <div className="join-modal-field">
              <label htmlFor="officialEmail">
                Official Email<span>*</span>
              </label>
              <input
                id="officialEmail"
                type="email"
                name="officialEmail"
                placeholder="Official Email"
                value={formData.officialEmail}
                onChange={handleChange}
                maxLength={254}
                className={errors.officialEmail ? 'join-modal-input-error' : ''}
              />
              {errors.officialEmail && (
                <p className="join-modal-error">{errors.officialEmail}</p>
              )}
            </div>
          </div>

          <div className="join-modal-row">
            <div className="join-modal-field">
              <label htmlFor="industry">
                Nature of Industry<span>*</span>
              </label>
              <SearchableDropdown
                id="industry"
                options={INDUSTRY_OPTIONS}
                value={formData.industry}
                onSelect={(label) => setField('industry', label)}
                placeholder="Select Industry"
                hasError={!!errors.industry}
              />
              {errors.industry && <p className="join-modal-error">{errors.industry}</p>}
            </div>
            <div className="join-modal-field">
              <label htmlFor="gender">
                Gender<span>*</span>
              </label>
              <select
                id="gender"
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                className={errors.gender ? 'join-modal-input-error' : ''}
              >
                <option value="" disabled>
                  Select Gender
                </option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
              {errors.gender && <p className="join-modal-error">{errors.gender}</p>}
            </div>
          </div>

          <div className="join-modal-field join-modal-field-full">
            <label htmlFor="linkedin">
              LinkedIn URL<span>*</span>
            </label>
            <input
              id="linkedin"
              type="url"
              name="linkedin"
              placeholder="https://www.linkedin.com/in/your-profile"
              value={formData.linkedin}
              onChange={handleChange}
              className={errors.linkedin ? 'join-modal-input-error' : ''}
            />
            {errors.linkedin && <p className="join-modal-error">{errors.linkedin}</p>}
          </div>

          <div className="join-modal-row">
            <div className="join-modal-field">
              <label htmlFor="state">
                State<span>*</span>
              </label>
              <SearchableDropdown
                id="state"
                options={STATE_OPTIONS}
                value={formData.state}
                onSelect={(label) => setField('state', label)}
                placeholder="Select State"
                hasError={!!errors.state}
              />
              {errors.state && <p className="join-modal-error">{errors.state}</p>}
            </div>
            <div className="join-modal-field">
              <label htmlFor="city">
                City<span>*</span>
              </label>
              <input
                id="city"
                type="text"
                name="city"
                placeholder="Enter City"
                value={formData.city}
                onChange={handleChange}
                className={errors.city ? 'join-modal-input-error' : ''}
              />
              {errors.city && <p className="join-modal-error">{errors.city}</p>}
            </div>
          </div>

          <div className="join-modal-field join-modal-field-full">
            <label htmlFor="join-modal-upload">
              Upload Your Image<span>*</span>
            </label>
            <div className={`join-modal-file ${errors.image ? 'join-modal-input-error' : ''}`}>
              <label className="join-modal-file-btn" htmlFor="join-modal-upload">
                Choose File
              </label>
              <span className="join-modal-file-name">
                {formData.image ? formData.image.name : 'No File Chosen'}
              </span>
              <input
                id="join-modal-upload"
                type="file"
                accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                onChange={handleFileChange}
              />
            </div>
            {errors.image && <p className="join-modal-error">{errors.image}</p>}
          </div>
 
          <button type="submit" className="join-modal-submit">
            Submit
          </button>
        </form>
      </div>
    </div>
  );
}

export default JoinModal;
